import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { API_ENDPOINTS } from '../../config/api';
import { apiFetch } from '../../utils/apiClient';
import {
  JH_BASE_VARIABLE_DEFS,
  mergeSavedPositions,
  buildDefaultOffsets,
  anchorPxToPositionPct,
  DRAFT_STORAGE_KEY
} from '../../config/jinghongProcessDiagramConfig';
import './ProcessDiagram.css';

const getCityDisplayName = (slug) => {
  const map = { linshui: '邻水', jinghong: '景洪' };
  return map[slug] || slug;
};

const ProcessDiagram = () => {
  const { city } = useParams();
  const cityName = getCityDisplayName(city);
  const [valueMap, setValueMap] = useState({});
  const [refreshMs, setRefreshMs] = useState(10000);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [layout, setLayout] = useState(null);
  const [layoutReady, setLayoutReady] = useState(false);
  const [calibrationMode, setCalibrationMode] = useState(false);
  const [offsetMap, setOffsetMap] = useState(() =>
    buildDefaultOffsets(mergeSavedPositions(JH_BASE_VARIABLE_DEFS, []))
  );
  const [selectedVarId, setSelectedVarId] = useState(JH_BASE_VARIABLE_DEFS[0]?.id || '');
  const canPersistDraft = useRef(false);
  const persistTimerRef = useRef(null);

  const calibrationLocked = !!layout?.calibrationLocked;

  const variableDefs = useMemo(() => {
    if (city !== 'jinghong') return [];
    return mergeSavedPositions(JH_BASE_VARIABLE_DEFS, layout?.positions);
  }, [city, layout]);

  const rows = useMemo(
    () =>
      variableDefs.map((v) => {
        const offsetPx = offsetMap[v.id] || { x: 0, y: 0 };
        const hasOffset = offsetPx.x !== 0 || offsetPx.y !== 0;
        const positionPct = hasOffset
          ? anchorPxToPositionPct(v.anchorPx.x + offsetPx.x, v.anchorPx.y + offsetPx.y)
          : v.positionPct;
        return {
          ...v,
          value: valueMap[v.name] ?? '-',
          offsetPx,
          positionPct
        };
      }),
    [variableDefs, valueMap, offsetMap]
  );

  const variableNames = useMemo(() => variableDefs.map((v) => v.name).filter(Boolean), [variableDefs]);

  useEffect(() => {
    if (city !== 'jinghong') return;
    if (!variableDefs.some((v) => v.id === selectedVarId)) {
      setSelectedVarId(variableDefs[0]?.id || '');
    }
  }, [variableDefs, selectedVarId, city]);

  useEffect(() => {
    if (city !== 'jinghong') return;
    setLayoutReady(false);
    let cancelled = false;
    (async () => {
      const fetchLayoutPayload = async () => {
        const bust = `_=${Date.now()}`;
        let apiData = null;
        try {
          const json = await apiFetch(`${API_ENDPOINTS.JINGHONG_PROCESS_LAYOUT}?${bust}`);
          if (json.success && json.data) apiData = json.data;
        } catch {
          // 后端未启动或未登录等
        }
        let staticData = null;
        try {
          const res = await fetch(`${window.location.origin}/config/jinghong-process-layout.json?${bust}`, {
            cache: 'no-store'
          });
          if (res.ok) staticData = await res.json();
        } catch {
          // 忽略
        }
        const apiHasPoints = apiData && Array.isArray(apiData.positions) && apiData.positions.length > 0;
        const staticHasPoints =
          staticData && Array.isArray(staticData.positions) && staticData.positions.length > 0;
        // 接口可能被浏览器/代理缓存成旧数据；静态文件与 npm run build 的 public 一致，作兜底
        if (apiHasPoints && staticHasPoints) {
          return {
            version: Math.max(apiData.version ?? 1, staticData.version ?? 1),
            calibrationLocked: !!(apiData.calibrationLocked || staticData.calibrationLocked),
            positions: staticData.calibrationLocked ? staticData.positions : apiData.positions
          };
        }
        if (apiHasPoints) return apiData;
        if (staticHasPoints) {
          return {
            version: staticData.version ?? 1,
            calibrationLocked: !!staticData.calibrationLocked,
            positions: staticData.positions
          };
        }
        if (apiData) return apiData;
        return { calibrationLocked: false, positions: [] };
      };

      try {
        const data = await fetchLayoutPayload();
        if (cancelled) return;
        setLayout({
          calibrationLocked: !!data.calibrationLocked,
          positions: Array.isArray(data.positions) ? data.positions : []
        });
        const defs = mergeSavedPositions(JH_BASE_VARIABLE_DEFS, data.positions);
        canPersistDraft.current = false;
        if (data.calibrationLocked) {
          setOffsetMap(buildDefaultOffsets(defs));
          setCalibrationMode(false);
        } else {
          const hasSavedPositions = Array.isArray(data.positions) && data.positions.length > 0;
          // 草稿里的 offset 是「相对当时基准锚点」的像素；若服务端已有正式 positions，再合并草稿会二次偏移，画面与已下载的 layout.json 不一致
          if (hasSavedPositions) {
            try {
              localStorage.removeItem(DRAFT_STORAGE_KEY);
            } catch {
              // ignore
            }
            setOffsetMap(buildDefaultOffsets(defs));
            setCalibrationMode(false);
          } else {
            try {
              const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
              if (raw) {
                const draft = JSON.parse(raw);
                const base = buildDefaultOffsets(defs);
                const merged = { ...base };
                for (const id of Object.keys(merged)) {
                  if (draft[id] && typeof draft[id].x === 'number' && typeof draft[id].y === 'number') {
                    merged[id] = { x: draft[id].x, y: draft[id].y };
                  }
                }
                setOffsetMap(merged);
              } else {
                setOffsetMap(buildDefaultOffsets(defs));
              }
            } catch {
              setOffsetMap(buildDefaultOffsets(defs));
            }
            setCalibrationMode(true);
          }
          canPersistDraft.current = true;
        }
      } catch {
        if (!cancelled) {
          setLayout({ calibrationLocked: false, positions: [] });
          setOffsetMap(buildDefaultOffsets(mergeSavedPositions(JH_BASE_VARIABLE_DEFS, [])));
          setCalibrationMode(true);
          canPersistDraft.current = true;
        }
      } finally {
        if (!cancelled) setLayoutReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [city]);

  useEffect(() => {
    if (city !== 'jinghong' || calibrationLocked || !canPersistDraft.current) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(offsetMap));
      } catch {
        // ignore quota / private mode
      }
    }, 400);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [offsetMap, city, calibrationLocked]);

  const fetchValues = async () => {
    try {
      const json = await apiFetch(API_ENDPOINTS.MQTT_VALUES, {
        method: 'POST',
        body: { variables: variableNames },
      });
      if (json && json.success) {
        setRefreshMs(json.refreshMs || 10000);
        const nextValueMap = {};
        json.data.forEach((d) => {
          nextValueMap[d.name] = d.value;
        });
        setValueMap(nextValueMap);
      }
    } catch {
      // 忽略网络错误，保留上次值
    }
  };

  useEffect(() => {
    if (city !== 'jinghong') return;
    fetchValues();
    const timer = setInterval(fetchValues, refreshMs);
    return () => clearInterval(timer);
  }, [city, refreshMs, variableNames.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const selectedOffset = offsetMap[selectedVarId] || { x: 0, y: 0 };

  const adjustOffset = (dx, dy) => {
    if (!selectedVarId || calibrationLocked) return;
    setOffsetMap((prev) => {
      const current = prev[selectedVarId] || { x: 0, y: 0 };
      return {
        ...prev,
        [selectedVarId]: { x: current.x + dx, y: current.y + dy }
      };
    });
  };

  const resetSelectedOffset = () => {
    if (!selectedVarId || calibrationLocked) return;
    setOffsetMap((prev) => ({
      ...prev,
      [selectedVarId]: { x: 0, y: 0 }
    }));
  };

  const applySelectedOffsetToAll = () => {
    if (!selectedVarId || calibrationLocked) return;
    const base = offsetMap[selectedVarId] || { x: 0, y: 0 };
    const next = {};
    variableDefs.forEach((v) => {
      next[v.id] = { x: base.x, y: base.y };
    });
    setOffsetMap(next);
  };

  const copyCalibrationJson = async () => {
    const payload = variableDefs.map((v) => ({
      id: v.id,
      name: v.name,
      anchorPx: {
        x: Math.round(v.anchorPx.x),
        y: Math.round(v.anchorPx.y)
      },
      offsetPx: offsetMap[v.id] || { x: 0, y: 0 }
    }));
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      // eslint-disable-next-line no-alert
      alert('已复制当前校准 JSON');
    } catch {
      // eslint-disable-next-line no-alert
      alert('复制失败，请手动从控制台获取');
      // eslint-disable-next-line no-console
      console.log(text);
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  /** 当前锚点 + 偏移 → 正式 positionPct 列表 */
  const buildMergedPositions = () =>
    variableDefs.map((v) => {
      const ox = offsetMap[v.id]?.x || 0;
      const oy = offsetMap[v.id]?.y || 0;
      return {
        id: v.id,
        positionPct: anchorPxToPositionPct(v.anchorPx.x + ox, v.anchorPx.y + oy)
      };
    });

  const buildLockedLayoutFilePayload = () => ({
    version: 1,
    calibrationLocked: true,
    positions: buildMergedPositions()
  });

  /** 无管理员权限时：下载 JSON，手动覆盖 frontend/public/config/jinghong-process-layout.json */
  const downloadPermanentLayoutFile = () => {
    if (calibrationLocked) return;
    const payload = buildLockedLayoutFilePayload();
    const text = `${JSON.stringify(payload, null, 2)}\n`;
    const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jinghong-process-layout.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    // eslint-disable-next-line no-alert
    alert(
      '已下载 jinghong-process-layout.json。\n请用其覆盖项目内文件：\ndiyaweb/frontend/public/config/jinghong-process-layout.json\n保存后刷新页面即可固定并关闭校准。'
    );
  };

  const commitPermanent = async () => {
    if (calibrationLocked) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        '将当前微调写入服务器上的正式配置文件，并永久关闭校准界面（仅管理员）。本地浏览器草稿会清除。确定？'
      )
    ) {
      return;
    }
    // eslint-disable-next-line no-alert
    alert('固定布局须在小程序管理端操作；Web 站仅公开展示。');
  };

  return (
    <div className="city-page">
      <Navbar />
      <main className="city-main">
        <div className="city-container">
          <h1 className="city-title">{cityName} · 工艺流程</h1>
          {city === 'jinghong' ? (
            <>
              <div className="diagram-image-container">
                <div className="diagram-image-frame">
                  <img
                    src="/images/jinghong-process-diagram.png?v=20260420-5"
                    alt="景洪工艺流程"
                    className="process-diagram-image"
                    onLoad={handleImageLoad}
                    onError={() => setImageLoaded(false)}
                  />

                  {imageLoaded && (
                    <div className="data-overlay">
                      {rows.map((row) => (
                        <div
                          key={row.id}
                          className="data-point"
                          style={{
                            left: `${row.positionPct.x}%`,
                            top: `${row.positionPct.y}%`
                          }}
                        >
                          {calibrationMode && !calibrationLocked && <div className="data-anchor-dot" />}
                          <div className="data-label">{row.label}</div>
                          <div className="data-value">{row.value ?? '-'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {layoutReady && calibrationMode && !calibrationLocked && (
                <div className="calibration-panel">
                  <div className="calibration-row">
                    <strong>校准模式</strong>
                    <span className="calibration-hint">偏移已自动暂存到本机浏览器</span>
                    <button type="button" onClick={() => setCalibrationMode(false)}>
                      关闭
                    </button>
                  </div>
                  <div className="calibration-row">
                    <label htmlFor="calibration-var">变量</label>
                    <select
                      id="calibration-var"
                      value={selectedVarId}
                      onChange={(e) => setSelectedVarId(e.target.value)}
                    >
                      {variableDefs.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="calibration-row">
                    <span>offsetX: {selectedOffset.x}</span>
                    <span>offsetY: {selectedOffset.y}</span>
                  </div>
                  <div className="calibration-row calibration-row-step100">
                    <button type="button" onClick={() => adjustOffset(-100, 0)}>
                      ←100
                    </button>
                    <button type="button" onClick={() => adjustOffset(100, 0)}>
                      100→
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, -100)}>
                      ↑100
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, 100)}>
                      100↓
                    </button>
                  </div>
                  <div className="calibration-row calibration-row-step10">
                    <button type="button" onClick={() => adjustOffset(-10, 0)}>
                      ←10
                    </button>
                    <button type="button" onClick={() => adjustOffset(10, 0)}>
                      10→
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, -10)}>
                      ↑10
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, 10)}>
                      10↓
                    </button>
                  </div>
                  <div className="calibration-row">
                    <button type="button" onClick={() => adjustOffset(-4, 0)}>
                      ←4
                    </button>
                    <button type="button" onClick={() => adjustOffset(4, 0)}>
                      4→
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, -4)}>
                      ↑4
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, 4)}>
                      4↓
                    </button>
                  </div>
                  <div className="calibration-row">
                    <button type="button" onClick={() => adjustOffset(-1, 0)}>
                      ←1
                    </button>
                    <button type="button" onClick={() => adjustOffset(1, 0)}>
                      1→
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, -1)}>
                      ↑1
                    </button>
                    <button type="button" onClick={() => adjustOffset(0, 1)}>
                      1↓
                    </button>
                  </div>
                  <div className="calibration-row">
                    <button type="button" onClick={resetSelectedOffset}>
                      重置当前
                    </button>
                    <button type="button" onClick={applySelectedOffsetToAll}>
                      应用到全部
                    </button>
                  </div>
                  <div className="calibration-row calibration-row-actions">
                    <button type="button" onClick={copyCalibrationJson}>
                      复制JSON
                    </button>
                    <button type="button" onClick={downloadPermanentLayoutFile}>
                      下载永久 layout.json
                    </button>
                    <button type="button" className="calibration-commit" onClick={commitPermanent}>
                      固定为永久并关闭校准
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="city-card">
              <p>这里展示{cityName}的工艺流程。后续可接入流程图、工艺段、设备信息等内容。</p>
            </div>
          )}
          {city === 'jinghong' && layoutReady && !calibrationMode && !calibrationLocked && (
            <div className="calibration-open">
              <button type="button" onClick={() => setCalibrationMode(true)}>
                打开校准模式
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProcessDiagram;
